import type { CoaApplyUndoResponse } from "@common/schemas/domains/accounting/chart-of-accounts-template-apply-event.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import type { UndoCoaTemplateApplyEventParams } from "@api/coaTemplateApplyEvent.api";
import type { CoaTemplateActivityEventRef } from "./coaTemplateActivityEvents";
import { formatCappedEntityLabel } from "./coaUndoCopy";

export type CoaTemplateGroupedUndoResult =
    | {
          status: "successful";
          eventRef: CoaTemplateActivityEventRef;
          response: CoaApplyUndoResponse;
      }
    | {
          status: "allSkipped";
          eventRef: CoaTemplateActivityEventRef;
          response: CoaApplyUndoResponse;
      }
    | {
          status: "failed";
          eventRef: CoaTemplateActivityEventRef;
      };

interface RunGroupedUndoParams {
    eventRefs: CoaTemplateActivityEventRef[];
    undoInvocationId: string;
    undoEvent: (
        params: UndoCoaTemplateApplyEventParams,
    ) => Promise<CoaApplyUndoResponse>;
    concurrency?: number;
}

export async function runCoaTemplateGroupedUndo({
    eventRefs,
    undoInvocationId,
    undoEvent,
    concurrency = 4,
}: RunGroupedUndoParams): Promise<CoaTemplateGroupedUndoResult[]> {
    const results: Array<CoaTemplateGroupedUndoResult | undefined> = new Array(
        eventRefs.length,
    );
    const workerCount = Math.max(1, Math.min(concurrency, eventRefs.length));
    let nextIndex = 0;

    const worker = async () => {
        while (nextIndex < eventRefs.length) {
            const index = nextIndex;
            nextIndex += 1;
            const eventRef = eventRefs[index];
            try {
                const response = await undoEvent({
                    workspaceId: eventRef.workspaceId,
                    eventId: eventRef.eventId,
                    undoInvocationId,
                });
                results[index] = {
                    status:
                        response.revertedCount > 0
                            ? "successful"
                            : "allSkipped",
                    eventRef,
                    response,
                };
            } catch {
                results[index] = { status: "failed", eventRef };
            }
        }
    };

    await Promise.all(Array.from({ length: workerCount }, worker));
    return results.filter(isGroupedUndoResult);
}

function isGroupedUndoResult(
    result: CoaTemplateGroupedUndoResult | undefined,
): result is CoaTemplateGroupedUndoResult {
    return result !== undefined;
}

export interface CoaTemplateGroupedUndoSummary {
    successfulEntities: number;
    skippedEntities: number;
    failedEntities: number;
    revertedActions: number;
    skippedActions: number;
    // Entity ids of the failed events, so the toast can name what to retry.
    failedEntityIds: number[];
}

export function getGroupedUndoSummary(
    results: CoaTemplateGroupedUndoResult[],
): CoaTemplateGroupedUndoSummary {
    let successfulEntities = 0;
    let skippedEntities = 0;
    let failedEntities = 0;
    let revertedActions = 0;
    let skippedActions = 0;
    const failedEntityIds: number[] = [];

    for (const result of results) {
        if (result.status === "successful") {
            successfulEntities += 1;
            revertedActions += result.response.revertedCount;
            skippedActions += result.response.skippedCount;
        } else if (result.status === "allSkipped") {
            skippedEntities += 1;
            skippedActions += result.response.skippedCount;
        } else {
            failedEntities += 1;
            failedEntityIds.push(result.eventRef.entityId);
        }
    }

    return {
        successfulEntities,
        skippedEntities,
        failedEntities,
        revertedActions,
        skippedActions,
        failedEntityIds,
    };
}

export type GroupedUndoToastSeverity = "success" | "warning" | "error";

export interface GroupedUndoToast {
    message: string;
    severity: GroupedUndoToastSeverity;
    // An all-failed undo needs to stay on screen: the drawer remains open and
    // the user has to act on it, so it must not auto-dismiss.
    isPersistent: boolean;
}

// Keeps the toast message and its severity derived from the same summary so an
// all-failed undo never surfaces as a green success toast — and never claims the
// undo completed.
export function getGroupedUndoToast({
    summary,
    entityNamesById,
}: {
    summary: CoaTemplateGroupedUndoSummary;
    entityNamesById: ReadonlyMap<number, string>;
}): GroupedUndoToast {
    if (summary.failedEntities > 0) {
        return getFailedUndoToast({ summary, entityNamesById });
    }
    if (summary.successfulEntities === 0) {
        return {
            message:
                summary.skippedEntities > 0
                    ? `Nothing to undo — ${describeSkippedEntities(summary)}`
                    : "Nothing to undo.",
            severity: "warning",
            isPersistent: false,
        };
    }
    const undidMessage = `Undid changes for ${pluralizeWithCount(
        "entity",
        summary.successfulEntities,
    )}.`;
    if (summary.skippedEntities === 0) {
        return {
            message: undidMessage,
            severity: "success",
            isPersistent: false,
        };
    }
    return {
        message: `${undidMessage} ${describeSkippedEntities(summary)}`,
        severity: "warning",
        isPersistent: false,
    };
}

function getFailedUndoToast({
    summary,
    entityNamesById,
}: {
    summary: CoaTemplateGroupedUndoSummary;
    entityNamesById: ReadonlyMap<number, string>;
}): GroupedUndoToast {
    const failedNames = summary.failedEntityIds.flatMap((entityId) => {
        const name = entityNamesById.get(entityId);
        return name ? [name] : [];
    });
    const failedLabel = formatCappedEntityLabel({
        names: failedNames,
        totalCount: summary.failedEntities,
    });
    const totalEntities =
        summary.successfulEntities +
        summary.skippedEntities +
        summary.failedEntities;
    if (summary.successfulEntities > 0 || summary.skippedEntities > 0) {
        return {
            message: `Couldn’t undo ${failedLabel} — ${
                summary.successfulEntities
            } of ${pluralizeWithCount("entity", totalEntities)} updated.`,
            severity: "warning",
            // Something failed, so the user still has work to do: this warning
            // stays put like the all-failed error rather than auto-dismissing.
            isPersistent: true,
        };
    }
    return {
        message: `Couldn’t undo ${failedLabel}. Review the drawer and try again.`,
        severity: "error",
        isPersistent: true,
    };
}

function describeSkippedEntities(
    summary: CoaTemplateGroupedUndoSummary,
): string {
    return `${pluralizeWithCount(
        "entity",
        summary.skippedEntities,
    )} had no eligible changes.`;
}
